import React from 'react';
import { useDroppable } from '@dnd-kit/core';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

// Bootstrap's own sort glyphs, so these read like the sort arrows on a table
// header rather than something invented for this board. The "-alt" variants are
// the descending forms (Z-A, 9-1); the idle button shows the ascending glyph
// greyed out, which is what it will do when clicked.
const SORT_ICONS = {
  name: { idle: 'bi-sort-alpha-down', asc: 'bi-sort-alpha-down', desc: 'bi-sort-alpha-down-alt' },
  amount: { idle: 'bi-sort-numeric-down', asc: 'bi-sort-numeric-down', desc: 'bi-sort-numeric-down-alt' },
};

const SORT_LABELS = {
  name: { idle: 'Sort by name', asc: 'Sorted A to Z', desc: 'Sorted Z to A' },
  amount: { idle: 'Sort by amount', asc: 'Sorted lowest first', desc: 'Sorted highest first' },
};

function SortButton({ sortKey, active, direction, onCycle }) {
  const state = active ? direction : 'idle';
  const label = SORT_LABELS[sortKey][state];

  return (
    <button
      type="button"
      className={`expense-kanban-column__sort${active ? ' expense-kanban-column__sort--active' : ''}`}
      onClick={() => onCycle(sortKey)}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      <i className={`bi ${SORT_ICONS[sortKey][state]}`} aria-hidden="true" />
    </button>
  );
}

/**
 * One bucket of the payroll board.
 *
 * While a card is in flight the column paints one of three states so the drop
 * target is obvious before the pointer gets there: `valid` (this move is
 * allowed), `override` (backwards, so it is refused but can be overruled) and
 * `blocked` (nothing will make this drop work).
 *
 * `onToggleIncludeCaps` is only passed to the Invoiced column, the one bucket
 * whose items can carry a cap on what we were allowed to bill; it switches the
 * total between what we paid and what we could invoice.
 */
export default function ExpenseKanbanColumn({
  id,
  title,
  count,
  total,
  dropState = null,
  sort = null,
  onCycleSort,
  includeCaps = false,
  onToggleIncludeCaps,
  children,
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  const className = [
    'expense-kanban-column',
    dropState ? `expense-kanban-column--${dropState}` : '',
    isOver && dropState ? 'expense-kanban-column--over' : '',
  ].filter(Boolean).join(' ');

  return (
    <section ref={setNodeRef} className={className} aria-label={title}>
      <header className="expense-kanban-column__head">
        <div className="expense-kanban-column__title-row">
          <h3 className="expense-kanban-column__title">{title}</h3>
          <span className="expense-kanban-column__count">{count}</span>
        </div>
        <div className="expense-kanban-column__total-row">
          <div className="expense-kanban-column__total">{formatCurrency(total)}</div>
          {onToggleIncludeCaps ? (
            <label className="expense-kanban-column__caps" title="Show what we were allowed to invoice instead of what we paid">
              <input
                type="checkbox"
                checked={includeCaps}
                onChange={(event) => onToggleIncludeCaps(event.target.checked)}
              />
              Enforce Caps
            </label>
          ) : null}
          {/* Nothing to order in an empty column, and the buttons would only be
              noise next to "Nothing here". */}
          {onCycleSort && count > 0 ? (
            <div className="expense-kanban-column__sorts">
              <SortButton
                sortKey="name"
                active={sort?.key === 'name'}
                direction={sort?.direction}
                onCycle={onCycleSort}
              />
              <SortButton
                sortKey="amount"
                active={sort?.key === 'amount'}
                direction={sort?.direction}
                onCycle={onCycleSort}
              />
            </div>
          ) : null}
        </div>
      </header>

      <div className="expense-kanban-column__body">
        {count === 0 ? (
          <div className="expense-kanban-column__empty">
            <i className="bi bi-inbox" aria-hidden="true" />
            <span>Nothing here</span>
          </div>
        ) : children}
      </div>

      {dropState === 'blocked' ? (
        <div className="expense-kanban-column__lock" aria-hidden="true">
          <i className="bi bi-slash-circle" />
        </div>
      ) : null}

      {dropState === 'override' ? (
        <div className="expense-kanban-column__lock expense-kanban-column__lock--override" aria-hidden="true">
          <i className="bi bi-arrow-counterclockwise" />
        </div>
      ) : null}
    </section>
  );
}
